import React, { useState, useEffect, useRef } from "react";
import { MoreVertical, Phone } from "lucide-react";
import ContactInfoPanel from "./ContactInfoPanel";
import AddContactPanel from "./Addcontactpanel";
import { useAPI } from "../Context/MyContext";
import CallPopup from "../Call/call";
import toast from "react-hot-toast";
import Avatar from "react-avatar";


const ChatHeader = ({ contact, number, phoneId, recipientId, messageID }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [isContactInfoOpen, setIsContactInfoOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const dropdownRef = useRef(null);

  const {
    toggleBotForConversation,
    fetchBotStatus,
    callState,
    startCall,
    cleanupCall,
    checkPermission,
    requestPermission
  } = useAPI();

  const [botEnabled, setBotEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  const [permissionModal, setPermissionModal] = useState({
    open: false,
    loading: false,
    data: null
  });

  const [permissionRequested, setPermissionRequested] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOptions]);

  // ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setPermissionModal({ open: false, loading: false, data: null });
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  // Load bot status
  useEffect(() => {
    if (!phoneId || !recipientId) return;

    let isMounted = true;

    const loadStatus = async () => {
      try {
        setLoading(true);

        const res = await fetchBotStatus({
          phoneNumberId: phoneId,
          messageID
        });

        if (isMounted) {
          setBotEnabled(res.botStatus);
        }
      } catch (err) {
        console.error("Failed to load bot status");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadStatus();

    return () => {
      isMounted = false;
    };
  }, [phoneId, recipientId]);

  const handleContactInfoClick = () => {
    setIsContactInfoOpen(true);
    setShowOptions(false);
  };

  const handleAddContactClick = () => {
    setIsContactInfoOpen(false);
    setIsAddContactOpen(true);
  };

  const handleSaveContact = (savedContact) => {
    const updatedContact = {
      ...contact,
      ...savedContact,
      isSaved: true
    };

    setIsAddContactOpen(false);
    setIsContactInfoOpen(true);
  };

  const handleToggleBot = async () => {
    if (loading || !phoneId || !recipientId) return;

    const newStatus = !botEnabled;

    try {
      setLoading(true);
      setBotEnabled(newStatus);

      await toggleBotForConversation({
        phoneNumberId: phoneId,
        messageID,
        status: newStatus
      });
    } catch (err) {
      console.error("Toggle failed");
      setBotEnabled(!newStatus);
    } finally {
      setLoading(false);
    }
  };

  const displayName = contact?.name?.trim() ? contact.name : number;

  const ensureCallPermission = async () => {
    const res = await checkPermission({
      to: recipientId,
      phone_number_id: phoneId
    });

    if (res.data?.canCall === false) {
      setPermissionModal({
        open: true,
        loading: false,
        data: { to: recipientId, phone_number_id: phoneId }
      });
      return false;
    }

    return true;
  };

  const handleStartCall = async () => {
    if (!phoneId || !recipientId) {
      toast.error("Missing phone or recipient information");
      return;
    }

    try {
      if (!(await ensureCallPermission())) return;

      const response = await startCall({
        to: recipientId,
        phone_number_id: phoneId
      });

      return response;
    } catch (error) {
      toast.error(
        <div>
          <div className="font-bold">
            {error?.response?.data?.action || "Failed to start call"}
          </div>
          <div>{error?.response?.data?.message || error.message}</div>
        </div>
      );

      console.error("Failed to start call:", error?.response?.data || error.message);
    }


  };


  const avatarColors = [
    "#94A3B8", // Slate
    "#A8A29E", // Stone
    "#9CA3AF", // Gray
    "#84A59D", // Sage
    "#B08968", // Brown
    "#7C8DB5", // Muted Blue
    "#A188A6", // Muted Purple
    "#8D99AE", // Cool Gray
  ];

  if (!contact) return null;

  return (
    <>
      <div className="p-3 border-b flex items-center justify-between bg-[#f0f2f5] z-[100] relative">
        <div className="flex items-center">
          <div className="relative mr-3">
            {/* <img
              src={
                contact.image ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`
              }
              alt={displayName}
              className="w-10 h-10 rounded-full object-cover"
            /> */}

            <Avatar
              // src={contact.image}     // Uses this if it exists
              name={displayName}      // Falls back to initials
              size="40"
              round={true}
              className="w-10 h-10"
              colors={avatarColors}
            />
          </div>

          <div>
            <div className="font-bold">{displayName}</div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            className={`text-gray-600 hover:text-gray-800 ${permissionRequested ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={handleStartCall}
            disabled={permissionRequested}
          >
            <Phone size={20} />
          </button>

          <div className="relative" ref={dropdownRef}>
            <button
              className="text-gray-600 hover:text-gray-800 p-1 rounded-full hover:bg-gray-200"
              onClick={() => setShowOptions(!showOptions)}
            >
              <MoreVertical size={25} />
            </button>

            {showOptions && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-xl py-2 z-[150] border">
                <div className="flex items-center justify-between px-4 py-2">
                  <div>
                    <div className="text-sm font-medium">Auto Reply (Bot)</div>
                    <div className="text-xs text-gray-500">Turn off to reply manually</div>
                  </div>

                  <button
                    onClick={handleToggleBot}
                    disabled={loading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${botEnabled ? "bg-[#25D366]" : "bg-gray-300"}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white ${botEnabled ? "translate-x-5" : "translate-x-1"}`}
                    />
                  </button>
                </div>

                <div className="border-t my-1" />

                <button
                  onClick={handleContactInfoClick}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                >
                  Contact info
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ContactInfoPanel
        contact={contact}
        messageID={messageID}
        number={number}
        phoneId={phoneId}
        isOpen={isContactInfoOpen}
        onClose={() => setIsContactInfoOpen(false)}
        onAddContact={handleAddContactClick}
      />

      <AddContactPanel
        contact={contact}
        isOpen={isAddContactOpen}
        onClose={() => setIsAddContactOpen(false)}
        onSave={handleSaveContact}
      />

      {callState && callState.status !== "idle" && (
        <CallPopup callState={callState} onEndCall={cleanupCall} />
      )}

      {permissionModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200]">
          <div className="bg-white rounded-xl p-6 w-[350px] shadow-xl">
            <h2 className="text-lg font-semibold mb-1">Call Permission Required</h2>

            <p className="text-sm text-gray-500 mb-5">
              This contact hasn’t granted permission yet. You can request access to continue.
            </p>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPermissionModal({ open: false, loading: false, data: null })}
                className="px-4 py-2 text-sm bg-gray-200 rounded"
              >
                Cancel
              </button>

              <button
                disabled={permissionModal.loading}
                onClick={async () => {
                  if (!permissionModal.data) return;

                  try {
                    setPermissionModal((prev) => ({ ...prev, loading: true }));

                    await requestPermission({
                      to: permissionModal.data.to,
                      phone_number_id: permissionModal.data.phone_number_id
                    });

                    setPermissionRequested(true);

                    setPermissionModal({ open: false, loading: false, data: null });

                    toast.success("Permission request sent");
                  } catch (err) {
                    toast.error("Failed to request permission");
                  }
                }}
                className="px-4 py-2 text-sm bg-[#25D366] text-white rounded"
              >
                {permissionModal.loading ? "Sending..." : "Request Permission"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatHeader;
